import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import lodash from 'lodash'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'

import { getPath } from 'components/Editor/EditorContext/selectors/block'
import Node from 'components/Editor/cource/layout/Node'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getNodeId } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import SliderContext, { type ISliderContextProps } from './SliderContext'
import * as s from './SliderElement.module.scss'
import Arrows from './components/Arrows'
import Progress from './components/Progress'
import SlidesList from './components/SlidesList'
import type { SliderElementType } from './sliderTypes'
import { ArrowPosition } from './sliderTypes'

const SliderElement: SliderElementType = ({
  block,
  element,
  styles,
  mode,
  state,
  setState,
  font,
  isV2,
}) => {
  const { items, hasLoop, type, hasProgress, includeSlide } = element.value
  const elementContainer = useRef<HTMLDivElement>(null)
  const index = items.findIndex((item) => item.value === state?.active)
  const slideIndex = index === -1 ? 0 : index
  const setSlideIndex = useCallback(
    (index: number) => setState?.({ active: items[index]?.value }),
    [items, setState],
  )
  const isPreview = mode.previewMode !== PreviewMode.editor

  const [viewedSlides, setViewedSlides] = useState<Record<string, boolean>>(
    items.reduce((acc, item, i) => ({ ...acc, [item.value]: i === 0 ? true : false }), {}),
  )
  const allViewed = isPreview && lodash.every(viewedSlides)

  const isMobile = mode.deviceMode === DeviceMode.mobile

  const isSlidePosition = styles.icons.sliderPosition === ArrowPosition.slide

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controlledProps: any = {}
  const activeId = useProjectContext(getNodeId)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.slider.type,
      elementId: element.id,
    },
  )

  if (activeId) {
    const path = getPath(block!, activeId)
    controlledProps.activeKey = items.findIndex((item) => path.some((id) => id === item.value))
  }

  const leftArrowNoLoop = !hasLoop && slideIndex === 0
  const rightArrowNoLoop = !hasLoop && slideIndex + 1 === items.length

  const nextSlide = useCallback(() => {
    const changeData = entityAnalyticsConfig.Interacted({
      resultExtensions: {
        fromSlide: items[slideIndex].value,
        toSlide: items[slideIndex + 1 < items.length ? slideIndex + 1 : 0].value,
      },
      objectExtensions: { slideCount: items.length, ...baseObjectExtensions },
      elementId: element.id,
    })
    sendXapiData(changeData)
    setSlideIndex(slideIndex + 1)
  }, [
    entityAnalyticsConfig,
    element.id,
    baseObjectExtensions,
    items,
    sendXapiData,
    setSlideIndex,
    slideIndex,
  ])

  const prevSlide = useCallback(() => {
    const newSlideIndex = slideIndex - 1 < 0 ? items.length - 1 : slideIndex - 1
    const changeData = entityAnalyticsConfig.Interacted({
      resultExtensions: {
        fromSlide: items[slideIndex].value,
        toSlide: items[newSlideIndex].value,
      },
      objectExtensions: { slideCount: items.length, ...baseObjectExtensions },
      elementId: element.id,
    })
    sendXapiData(changeData)
    setSlideIndex(newSlideIndex)
  }, [
    slideIndex,
    items,
    element.id,
    entityAnalyticsConfig,
    baseObjectExtensions,
    sendXapiData,
    setSlideIndex,
  ])

  const goToSlide = useCallback(
    (number: number) => setSlideIndex(number % items.length),
    [items.length, setSlideIndex],
  )

  const contextValue = useMemo(
    (): ISliderContextProps => ({
      goToSlide,
      nextSlide,
      prevSlide,
      slideIndex: slideIndex || 0,
      items,
      mode,
      leftArrowNoLoop,
      rightArrowNoLoop,
      includeSlide,
    }),
    [
      goToSlide,
      includeSlide,
      items,
      leftArrowNoLoop,
      mode,
      nextSlide,
      prevSlide,
      rightArrowNoLoop,
      slideIndex,
    ],
  )

  const isPdf = mode.previewMode === PreviewMode.pdf

  useEffect(() => {
    if (typeof controlledProps.activeKey === 'number' && controlledProps.activeKey !== -1) {
      if (slideIndex !== controlledProps.activeKey) {
        goToSlide(controlledProps.activeKey)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledProps.activeKey])

  useEffect(() => {
    if (!slideIndex) {
      setSlideIndex(0)
    }

    if (!allViewed && items[slideIndex]) {
      setViewedSlides((prev) => ({ ...prev, [items[slideIndex].value]: true }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, slideIndex])

  useEffect(() => {
    if (slideIndex >= items.length) {
      setSlideIndex(items.length - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  useElementCompleted({
    block,
    elementId: element.id,
    completed: allViewed,
    deviceMode: mode.deviceMode,
  })

  if (isPdf) {
    const slidersComponent = (
      <div className={s.pdfContainer}>
        {items.map((item) => (
          <Node block={block!} id={item.value} key={index} mode={mode} isItem />
        ))}
      </div>
    )
    return (
      <SliderContext.Provider value={contextValue}>
        <div className={s.root} ref={elementContainer}>
          {hasProgress && isSlidePosition && (
            <Progress font={font} includeSlide={includeSlide} styles={styles} type={type} />
          )}
          {isSlidePosition && !isMobile && (
            <Arrows styles={styles}>
              <div className={s.slideArrow}>{slidersComponent}</div>
            </Arrows>
          )}
          {isSlidePosition && isMobile && items.length && slidersComponent}
        </div>
      </SliderContext.Provider>
    )
  }

  return (
    <div
      aria-label='Slider'
      aria-roledescription='Slider'
      className={cn(s.root, { [s.isV2]: isV2 })}
      ref={elementContainer}
      role='region'
    >
      <SliderContext.Provider value={contextValue}>
        {hasProgress && isSlidePosition && (
          <Progress font={font} includeSlide={includeSlide} styles={styles} type={type} />
        )}
        {isSlidePosition && !isMobile && (
          <Arrows isV2={isV2} styles={styles}>
            <div className={cn(s.slideArrow, { [s.isV2]: isV2 })}>
              <SlidesList block={block!} isV2={isV2} />
            </div>
          </Arrows>
        )}
        {isSlidePosition && isMobile && <SlidesList block={block!} isV2={isV2} />}
        {!isSlidePosition && (
          <>
            <Arrows isV2={isV2} styles={styles}>
              {hasProgress && (
                <Progress font={font} includeSlide={includeSlide} styles={styles} type={type} />
              )}
            </Arrows>
            <SlidesList block={block!} isV2={isV2} />
          </>
        )}
      </SliderContext.Provider>
    </div>
  )
}

export default SliderElement
